import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, errorText } from '../api';
import { useLive, type LiveType } from './live';

export interface ApiState<T> {
  data: T | undefined;
  error: string | null;
  // HTTP-Status des letzten Fehlers (z. B. 403/404 = kein Zugriff), 0 = keine Verbindung
  errorStatus: number | null;
  // Code des letzten Fehlers vom Server (z. B. „tarif_feature“), null ohne Code
  errorCode: string | null;
  loading: boolean;
  reload(): Promise<void>;
  setData(updater: (prev: T | undefined) => T | undefined): void;
}

// Lädt Daten von der API und lädt neu, wenn ein passendes Live-Ereignis kommt.
// url = null: nichts laden.
export function useApi<T>(url: string | null, opts: { live?: LiveType[]; clientId?: string } = {}): ApiState<T> {
  const [data, setDataState] = useState<T | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(url !== null);
  const current = useRef(url);
  current.current = url;

  const reload = useCallback(async () => {
    const u = current.current;
    if (!u) return;
    try {
      const d = await api.get<T>(u);
      if (current.current === u) {
        setDataState(d);
        setError(null);
        setErrorStatus(null);
        setErrorCode(null);
      }
    } catch (err) {
      if (current.current === u) {
        setError(errorText(err));
        setErrorStatus(err instanceof ApiError ? err.status : null);
        setErrorCode(err instanceof ApiError ? (err.code ?? null) : null);
      }
    } finally {
      if (current.current === u) setLoading(false);
    }
  }, []);

  useEffect(() => {
    setDataState(undefined);
    setError(null);
    setErrorStatus(null);
    setErrorCode(null);
    setLoading(url !== null);
    void reload();
  }, [url, reload]);

  useLive(opts.live ?? null, (e) => {
    if (opts.clientId && e.clientId && e.clientId !== opts.clientId) return;
    void reload();
  });

  const setData = useCallback((u: (prev: T | undefined) => T | undefined) => setDataState(u), []);
  return { data, error, errorStatus, errorCode, loading, reload, setData };
}
